// EventList.tsx - Updated to use EventsContext
import { useLocalization } from '@ais-ui/locale-management';
import {
  Accordion,
  CardContent,
  CardDescription,
  CardFooter,
  InfiniteScroll,
  Input,
  LoadingSpinner,
  ScrollArea,
  TrailingDateTimePickerWithRange,
} from '@ais-ui/ui-core';
import { useTimeZoneContext } from '@ais-ui/utils';
import { DetectedEvent } from '@ais-ui/websockets';
import React, { useMemo, useState } from 'react';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { EventActionsMenu } from './EventActionsMenu';
import {
  CombinedGroundingEventAndConfiguration,
  GroundingEventItem,
} from './Detectors/Grounding/GroundingEvent';
import { useEventFilters } from './useEventFilters';
import { EventFilter } from './EventFilter';
import { EventSort } from './EventSort';
import { NoResultsEmptyStateList } from '../../components/EmptyList';
import { EventItem } from './EventItem';
import { DroneEventItem } from './DroneEventItem';

const isDroneEvent = (event: DetectedEvent): boolean => {
  return event.type_of_mobile === 'Drone Sensor';
};
import { useEventsContext } from './EventsContext';

export const EventList = () => {
  const { messages } = useLocalization();
  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();
  const [search, setSearch] = useState<string>('');

  const {
    events,
    groundingEvents,
    isLoading,
    date,
    setDate,
    isTrailing,
    setIsTrailing,
    defaultLookBackTime,
  } = useEventsContext();

  const {
    combinedEvents,
    eventFilters,
    setEventFilters,
    sortConfig,
    setSortConfig,
  } = useEventFilters(events, groundingEvents, search);

  const isLoadingAnyEvents = isLoading && events.length === 0;

  // Count active events for the subtitle
  const activeCount = useMemo(() => {
    return events?.filter((e) => !e.deactivated).length ?? 0;
  }, [events]);

  return (
    <>
      <CardContent className="space-y-4">
        <CardDescription>
          {events && events.length > 0
            ? `${events.length} events (${activeCount} active)`
            : messages.eventsListSubtitle}
        </CardDescription>
        <TrailingDateTimePickerWithRange
          date={date}
          setDate={setDate}
          timeZone={timeZone}
          dateTimeFormat={userDateFormat}
          entity="event"
          isTrailing={isTrailing}
          setIsTrailing={setIsTrailing}
          lookBackTimeInMinutes={defaultLookBackTime}
        />
        <div className="flex gap-2">
          <Input
            id="search"
            placeholder="Find events..."
            className="h-10 flex-1"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <EventSort sortConfig={sortConfig} setSortConfig={setSortConfig} />
          <EventFilter
            eventFilters={eventFilters}
            setEventFilters={setEventFilters}
          />
        </div>
      </CardContent>
      <CardContent className="flex-1 flex flex-col overflow-hidden">
        {combinedEvents.length === 0 && isLoadingAnyEvents && (
          <LoadingSpinner />
        )}
        {combinedEvents.length === 0 && !isLoadingAnyEvents && (
          <NoResultsEmptyStateList
            entity="event"
            srcImage="empty_list_bro.png"
          />
        )}
        {combinedEvents.length > 0 && (
          <Accordion
            className="flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            <InfiniteScroll
              items={combinedEvents}
              limit={20}
              observeResize={true}
              className="flex-grow p-0 overflow-y-auto border rounded-lg"
              renderItem={(combinedEvent) =>
                combinedEvent.type === 'regular' ? (
                  isDroneEvent(combinedEvent.event as DetectedEvent) ? (
                    <DroneEventItem
                      key={`drone-${combinedEvent.id}`}
                      event={combinedEvent.event as DetectedEvent}
                    />
                  ) : (
                    <EventItem
                      key={`regular-${combinedEvent.id}`}
                      event={combinedEvent.event as DetectedEvent}
                    />
                  )
                ) : (
                  <GroundingEventItem
                    key={`grounding-${combinedEvent.id}`}
                    event={
                      (
                        combinedEvent.event as CombinedGroundingEventAndConfiguration
                      ).event
                    }
                    configuration={
                      (
                        combinedEvent.event as CombinedGroundingEventAndConfiguration
                      ).configuration
                    }
                  />
                )
              }
            />
          </Accordion>
        )}
      </CardContent>
      <CardFooter className="flex flex-grow-0 flex-col items-end">
        <EventActionsMenu start={date?.from} end={date?.to} />
      </CardFooter>
    </>
  );
};
